import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { APP_STORE_URL, appStoreReviews, parentGuides } from "./content";
import usePageMetadata from "./usePageMetadata";

const groups = [
    { title: "By age", links: parentGuides.slice(0, 3) },
    { title: "By skill", links: parentGuides.slice(3, 5) },
    { title: "Features", links: parentGuides.slice(5) },
];

export default function MathGamesHub() {
    const [activeReview, setActiveReview] = useState(0);
    const [reviewsPerView, setReviewsPerView] = useState(1);
    const lastReviewPosition = Math.max(0, appStoreReviews.length - reviewsPerView);

    useEffect(() => {
        const tabletQuery = window.matchMedia("(min-width: 640px)");
        const desktopQuery = window.matchMedia("(min-width: 1280px)");

        const updateReviewsPerView = () => {
            const nextReviewsPerView = desktopQuery.matches ? 3 : tabletQuery.matches ? 2 : 1;
            setReviewsPerView(nextReviewsPerView);
            setActiveReview((current) => Math.min(current, Math.max(0, appStoreReviews.length - nextReviewsPerView)));
        };

        updateReviewsPerView();
        tabletQuery.addEventListener("change", updateReviewsPerView);
        desktopQuery.addEventListener("change", updateReviewsPerView);

        return () => {
            tabletQuery.removeEventListener("change", updateReviewsPerView);
            desktopQuery.removeEventListener("change", updateReviewsPerView);
        };
    }, []);

    usePageMetadata({
        title: "Math Games for Kids",
        description: "Find age-appropriate MathMinds games and parent guides for multiplication, fractions, offline learning, and ad-free math practice.",
    });

    return (
        <div className="mx-auto grid max-w-[1200px] gap-10 px-5 py-12 sm:px-8 lg:grid-cols-[250px_1fr] lg:py-16">
            <aside className="self-start rounded-3xl border border-[#E9E2CD] bg-white p-6 lg:sticky lg:top-24" aria-label="Parent guide navigation">
                <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#C37C00]">For parents</p>
                {groups.map((group) => (
                    <section key={group.title} className="mt-6">
                        <h2 className="font-bold text-[#283C43]">{group.title}</h2>
                        <ul className="mt-2 space-y-2">
                            {group.links.map((link) => (
                                <li key={link.path}>
                                    <NavLink to={link.path} className="text-sm text-[#327560] hover:underline">{link.label}</NavLink>
                                </li>
                            ))}
                        </ul>
                    </section>
                ))}
            </aside>

            <article>
                <p className="font-bold uppercase tracking-[0.18em] text-[#C37C00]">MathMinds parent guides</p>
                <h1 className="mt-3 font-chango text-4xl leading-tight text-[#327560] sm:text-5xl">Math games for kids</h1>
                <p className="mt-6 max-w-3xl text-lg leading-8 text-[#56635E]">
                    Choose games by age, learning skill, or the way your family likes to play. These guides help parents match practice to a child’s current confidence and goals.
                </p>

                <div className="mt-10 grid gap-5 md:grid-cols-2">
                    {parentGuides.map((guide) => (
                        <NavLink key={guide.path} to={guide.path} className="rounded-3xl border border-[#E9E2CD] bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-[#86B7A5]">
                            <h2 className="text-xl font-bold text-[#327560]">{guide.label}</h2>
                            <p className="mt-3 leading-7 text-[#56635E]">{guide.description}</p>
                            <span className="mt-5 inline-block font-bold text-[#327560]">Read guide →</span>
                        </NavLink>
                    ))}
                </div>

                <section className="mt-12 rounded-3xl bg-[#DDF5ED] p-8">
                    <h2 className="font-chango text-2xl text-[#327560]">Ready to play?</h2>
                    <p className="mt-3 leading-7 text-[#56635E]">Explore the complete MathMinds game menu and choose a skill to practice.</p>
                    <NavLink to="/games/memory" className="mt-6 inline-block rounded-full bg-[#F9B818] px-6 py-3 font-bold hover:bg-[#FFCA47]">Open the games</NavLink>
                    <NavLink to="/#daily-challenges" className="ml-3 mt-6 inline-block rounded-full border-2 border-[#327560] px-6 py-2.5 font-bold text-[#327560] hover:bg-white/50">Learn about daily challenges</NavLink>
                </section>

                <section id="parent-reviews" className="mt-12 scroll-mt-24 border-t border-[#E9E2CD] pt-12">
                    <p className="font-bold uppercase tracking-[0.18em] text-[#C37C00]">Selected public App Store reviews</p>
                    <h2 className="mt-3 font-chango text-3xl text-[#327560]">What Parents Say About MathMinds</h2>
                    <p className="mt-5 max-w-3xl leading-8 text-[#56635E]">Every child learns differently. MathMinds makes math practice feel more like play, with interactive activities for counting, arithmetic, multiplication, fractions, memory, and concentration.</p>

                    <div className="mt-8" role="region" aria-roledescription="carousel" aria-label="Selected App Store reviews">
                        <div className="overflow-hidden">
                            <div
                                className="flex items-stretch gap-5 transition-transform duration-500 ease-out motion-reduce:transition-none"
                                style={{ transform: `translateX(calc(-${activeReview} * ((100% + 1.25rem) / ${reviewsPerView})))` }}
                            >
                                {appStoreReviews.map((review, index) => (
                                    <figure
                                        key={review.title}
                                        className="flex shrink-0 flex-col rounded-3xl bg-white p-6 shadow-sm sm:p-7 xl:p-8"
                                        style={{ flexBasis: `calc((100% - ${(reviewsPerView - 1) * 1.25}rem) / ${reviewsPerView})` }}
                                        aria-roledescription="slide"
                                        aria-label={`${index + 1} of ${appStoreReviews.length}`}
                                        aria-hidden={index < activeReview || index >= activeReview + reviewsPerView}
                                    >
                                        <h3 className="text-xl font-bold text-[#327560]">{review.title}</h3>
                                        <blockquote className="mt-4 flex-1 text-base leading-7 text-[#56635E] sm:text-lg sm:leading-8 xl:text-base xl:leading-7">“{review.quote}”</blockquote>
                                        <figcaption className="mt-6 text-sm font-semibold text-[#6B756F]">App Store review · {review.date}</figcaption>
                                    </figure>
                                ))}
                            </div>
                        </div>

                        <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
                            <button
                                type="button"
                                onClick={() => setActiveReview((current) => Math.max(0, current - 1))}
                                disabled={activeReview === 0}
                                className="order-2 rounded-full border-2 border-[#327560] px-4 py-2 text-sm font-bold text-[#327560] transition hover:bg-[#DDF5ED] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#327560] disabled:cursor-not-allowed disabled:opacity-40 sm:order-1 sm:px-5 sm:text-base"
                                aria-label="Show previous review"
                            >
                                ← Previous
                            </button>

                            <div className="order-1 flex w-full items-center justify-center gap-3 sm:order-2 sm:w-auto" aria-label="Choose a review position">
                                {Array.from({ length: lastReviewPosition + 1 }, (_, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => setActiveReview(index)}
                                        className={`h-3 w-3 rounded-full transition ${index === activeReview ? "bg-[#327560]" : "bg-[#C8D8D2] hover:bg-[#86B7A5]"}`}
                                        aria-label={`Show reviews starting at review ${index + 1}`}
                                        aria-current={index === activeReview ? "true" : undefined}
                                    />
                                ))}
                            </div>

                            <button
                                type="button"
                                onClick={() => setActiveReview((current) => Math.min(lastReviewPosition, current + 1))}
                                disabled={activeReview === lastReviewPosition}
                                className="order-3 rounded-full border-2 border-[#327560] px-4 py-2 text-sm font-bold text-[#327560] transition hover:bg-[#DDF5ED] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#327560] disabled:cursor-not-allowed disabled:opacity-40 sm:px-5 sm:text-base"
                                aria-label="Show next review"
                            >
                                Next →
                            </button>
                        </div>
                        <p className="sr-only" aria-live="polite">Showing reviews {activeReview + 1} through {Math.min(activeReview + reviewsPerView, appStoreReviews.length)} of {appStoreReviews.length}</p>
                    </div>

                    <p className="mt-8 leading-7 text-[#56635E]">We appreciate families who take the time to share their feedback. Their experiences help us continue improving MathMinds and making learning through play more enjoyable.</p>
                    <p className="mt-4 text-sm leading-6 text-[#6B756F]">These are selected excerpts from publicly visible App Store reviews. They are not presented as a representative survey of all MathMinds customers, and MathMinds does not claim that every reviewer is a verified parent.</p>
                    <p className="mt-5 font-bold text-[#327560]">Want to see more feedback? <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="underline">Read reviews on the MathMinds App Store page ↗</a></p>
                </section>
            </article>
        </div>
    );
}
